import React, { useEffect, useState } from 'react';
import { Button, Typography, Box } from '@mui/material';
import { FormControlTimeSelectFieldProps, FormElementControlPropsType } from './index';
import useBundleTranslation from 'i18n';
import { nanoid } from '@reduxjs/toolkit';
import ReactSelect from 'components/common/react-select/ReactSelect';
import styles from './FormDateTimeSelect.styles';
import IconMi from 'components/common/icon/IconMi';
import { FormComponentBuilder } from 'components/common/form/layout';

const hoursRange = Array.from(Array(24).keys()).map((item) => ({
    label: `${item}`,
    value: `${item}`,
    id: nanoid(),
}));

const minutesRange = Array.from(Array(60).keys()).map((item) => ({
    label: item <= 9 ? `0${item}` : `${item}`,
    value: item <= 9 ? `0${item}` : `${item}`,
}));

export default function FormTimeSelect({ controlProps }: FormElementControlPropsType<FormControlTimeSelectFieldProps>) {
    const { t } = useBundleTranslation('app/distribution/notification_schedules');
    const [hoursValue, setHoursValue] = useState<string | string[]>(
        typeof controlProps.form.hookFormGetValues(controlProps.hoursFieldName) != undefined
            ? controlProps.form.hookFormGetValues(controlProps.hoursFieldName)
            : [hoursRange[8].value],
    );
    const [minutesValue, setMinutesValue] = useState<string | string[]>(
        typeof controlProps.form.hookFormGetValues(controlProps.minutesFieldName) != undefined
            ? controlProps.form.hookFormGetValues(controlProps.minutesFieldName)
            : [minutesRange[0].value],
    );

    useEffect(() => {
        controlProps.form.hookFormSetValue(controlProps.hoursFieldName, hoursValue, { shouldDirty: true });
        controlProps.form.hookFormSetValue(controlProps.minutesFieldName, minutesValue, { shouldDirty: true });
    }, [hoursValue, minutesValue]);

    const addNewTime = () => {
        setHoursValue((prev) => {
            return [...prev, hoursRange[8].value];
        });
        setMinutesValue((prev) => {
            return [...prev, minutesRange[0].value];
        });
    };

    const removeTime = (indexToRemove: number) => {
        setHoursValue((prev) => {
            const newHoursValue = [...prev];
            newHoursValue.splice(indexToRemove, 1);
            return newHoursValue;
        });
        setMinutesValue((prev) => {
            const newMinutesValue = [...prev];
            newMinutesValue.splice(indexToRemove, 1);
            return newMinutesValue;
        });
    };

    const updateHours = (indexToUpdate: number, newValue: string) => {
        setHoursValue((prev) => {
            const newHoursValue = [...prev];
            newHoursValue.splice(indexToUpdate, 1, newValue);
            return newHoursValue;
        });
    };

    const updateMinutes = (indexToUpdate: number, newValue: string) => {
        setMinutesValue((prev) => {
            const newMinutesValue = [...prev];
            newMinutesValue.splice(indexToUpdate, 1, newValue);
            return newMinutesValue;
        });
    };

    return (
        <Box>
            <Typography variant="body1" gutterBottom component="div">
                {t(controlProps.label as string)}
            </Typography>
            {Array.isArray(hoursValue) ? (
                <>
                    {hoursValue.map((_, index: number) => {
                        return (
                            <Box
                                sx={{ ...styles.timeField, ...styles.field }}
                                key={hoursRange[index].id}
                                className="multiple"
                            >
                                <Box sx={styles.selectBox}>
                                    <ReactSelect
                                        data={hoursRange}
                                        value={hoursValue[index]}
                                        name={controlProps.hoursFieldName}
                                        update={(value: string) => updateHours(index, value)}
                                    />
                                </Box>
                                <Typography variant="body1" sx={styles.separator} gutterBottom component="div">
                                    :
                                </Typography>
                                <Box sx={styles.selectBox}>
                                    <ReactSelect
                                        data={minutesRange}
                                        value={minutesValue[index]}
                                        name={controlProps.minutesFieldName}
                                        update={(value: string) => updateMinutes(index, value)}
                                    />
                                </Box>
                                {hoursValue.length > 1 && (
                                    <IconMi onClick={() => removeTime(index)} icon={'times'} fontSize={'16'} />
                                )}
                            </Box>
                        );
                    })}
                </>
            ) : (
                <Box sx={styles.timeField}>
                    <Box sx={styles.selectBox}>
                        <ReactSelect
                            data={hoursRange}
                            value={hoursValue}
                            name={controlProps.hoursFieldName}
                            update={(value: string) => setHoursValue(value)}
                        />
                    </Box>
                    <Typography variant="body1" sx={styles.separator} gutterBottom component="div">
                        :
                    </Typography>
                    <Box sx={styles.selectBox}>
                        <ReactSelect
                            data={minutesRange}
                            value={minutesValue}
                            name={controlProps.minutesFieldName}
                            update={(value: string) => setMinutesValue(value)}
                        />
                    </Box>
                </Box>
            )}

            {controlProps.multiple && (
                <Button sx={{ mt: 1 }} onClick={addNewTime} startIcon={<IconMi icon={'new'} />} variant={'text'}>
                    {t('add_new_time')}
                </Button>
            )}
        </Box>
    );
}

export class FormTimeSelectBuilder extends FormComponentBuilder {
    prepareProps(): FormControlTimeSelectFieldProps {
        return {
            ...this.controlProps,
            multiple: this.elementProps.componentProps?.multiple ?? false,
            hoursValue: this.elementProps.componentProps?.hoursValue ?? '',
            minutesValue: this.elementProps.componentProps?.minutesValue ?? '',
            hoursFieldName: this.elementProps.componentProps?.hours_field_name ?? '',
            minutesFieldName: this.elementProps.componentProps?.minutes_field_name ?? '',
        };
    }
}
